import * as React from "react";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { Avatar } from "../../../Avatar";
import { ListViewCard } from "../ListViewCard";
import { normalizeAvatarUrl, shortDate } from "../DeckCard/logic/formatters";
import type { ListViewCardAction, ListViewCardBadge, ListViewCardLayout, ListViewCTAGroup } from "../ListViewCard";
import { iconChecking } from "../../../SmartLinkSyncStatusBadge/SmartLinkSyncStatusBadge.css";

export interface ListViewUserCardUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string | null;
  lockedAt?: string | null;
  deletedAt?: string | null;
  createdAt?: string;
  pendingMembershipOrgs?: Array<{ id: string; name: string; logoUrl?: string | null }>;
  /** Cheap, pre-computed Smart Link sync-status badge signal — undefined/null when not linked. */
  smartLinkSyncStatus?: "in_sync" | "out_of_sync" | "unknown" | null;
}

// Same icon-per-state + color pairing as SmartLinkSyncStatusBadge (used on
// the table view and user detail page) -- label stays "Smart Link" in every
// state, only the icon shape and color denote status. This card badge shape
// only accepts a single accent color (background is auto-derived as a tint
// of it, same as the role-color badges below), so no dark-mode variant here
// -- matches how role.color badges already behave in this same component.
const SMART_LINK_BADGE_META: Record<
  "in_sync" | "out_of_sync" | "unknown",
  { color: string; icon: React.ReactNode; title: string }
> = {
  in_sync: { color: "#047857", icon: <CheckCircle2 size={12} strokeWidth={2.5} />, title: "Synced" },
  out_of_sync: { color: "#B91C1C", icon: <XCircle size={12} strokeWidth={2.5} />, title: "Out of sync" },
  // Spins (via the shared iconChecking class) to signal a check is likely
  // in flight -- the opportunistic sweep targets exactly this status, and
  // the badge resolves on its own via a live update once it completes.
  unknown: {
    color: "#6B7280",
    icon: <RefreshCw size={12} strokeWidth={2.5} className={iconChecking} />,
    title: "Checking…",
  },
};

export interface ListViewUserCardProps {
  layout: ListViewCardLayout;
  user: ListViewUserCardUser;
  contextActions?: ListViewCardAction[];
  onClick?: () => void;
  ctaGroups?: ListViewCTAGroup[];
  animatedHighlight?: boolean;
}

export function ListViewUserCard({ layout, user, contextActions, onClick, ctaGroups, animatedHighlight }: ListViewUserCardProps) {
  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email;

  const avatarSrc = normalizeAvatarUrl(user.avatarUrl) || undefined;
  const isPendingMember = (user.pendingMembershipOrgs?.length ?? 0) > 0;

  const image = (
    <Avatar
      email={user.email}
      firstName={user.firstName}
      lastName={user.lastName}
      image={avatarSrc}
      size={layout === "deck" ? 100 : 72}
    />
  );

  const statusBadge = user.deletedAt
    ? { id: "status", label: "Disabled", variant: "disabled" as const }
    : user.lockedAt
    ? { id: "status", label: "Locked", variant: "pending" as const }
    : isPendingMember
    ? { id: "status", label: "Pending", variant: "pending" as const }
    : { id: "status", label: "Active", variant: "active" as const };

  const smartLinkBadge: ListViewCardBadge | null = user.smartLinkSyncStatus
    ? {
        id: "smart-link",
        label: "Smart Link",
        color: SMART_LINK_BADGE_META[user.smartLinkSyncStatus].color,
        icon: SMART_LINK_BADGE_META[user.smartLinkSyncStatus].icon,
        title: SMART_LINK_BADGE_META[user.smartLinkSyncStatus].title,
      }
    : null;

  const footerText = user.createdAt ? `Joined ${shortDate(user.createdAt)}` : undefined;

  return (
    <ListViewCard
      id={user.id}
      layout={layout}
      imageFormat="avatar"
      image={image}
      title={displayName}
      subtitle={user.email}
      badges={smartLinkBadge ? [statusBadge, smartLinkBadge] : [statusBadge]}
      footerText={footerText}
      onClick={onClick}
      contextActions={contextActions}
      ctaGroups={ctaGroups}
      animatedHighlight={animatedHighlight ?? isPendingMember}
    />
  );
}
